import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Chrome, KeyRound } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FadeUp } from "../components/Reveal";

export default function PortalProfile() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/auth/profile", form);
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="max-w-xl" data-testid="portal-profile">
      <FadeUp>
        <h1 className="font-serif text-3xl sm:text-4xl text-obsidian">Your <span className="italic text-purple-700">profile</span></h1>
      </FadeUp>
      <FadeUp delay={0.1} className="mt-8 bg-white rounded-3xl border border-purple-100 p-8">
        <form onSubmit={save} className="space-y-5">
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest text-slate-400">Name</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input"
              className="mt-2 w-full rounded-xl border border-purple-100 px-4 py-3 text-sm focus:outline-none focus:border-purple-400" />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest text-slate-400">Phone (text confirmations)</span>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone-input"
              className="mt-2 w-full rounded-xl border border-purple-100 px-4 py-3 text-sm focus:outline-none focus:border-purple-400" />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest text-slate-400">Email</span>
            <input value={user?.email} disabled data-testid="profile-email-input"
              className="mt-2 w-full rounded-xl border border-purple-50 bg-lilac-mist px-4 py-3 text-sm text-slate-400" />
          </label>
          <div className="flex items-center gap-2 pt-1">
            {(user?.auth_providers || []).map((p) => (
              <span key={p} className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-full bg-lilac-mist text-purple-700" data-testid={`provider-${p}`}>
                {p === "google" ? <Chrome size={11} /> : <KeyRound size={11} />} {p}
              </span>
            ))}
          </div>
          <button type="submit" disabled={saving} data-testid="profile-save-btn"
            className="px-7 py-3.5 rounded-full bg-obsidian text-white text-sm hover:text-gold transition-colors disabled:opacity-50">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </form>
      </FadeUp>
      <FadeUp delay={0.15} className="mt-6">
        <button onClick={doLogout} data-testid="profile-logout-btn"
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-red-500 transition-colors">
          <LogOut size={15} /> Sign out of Mixed By Empress
        </button>
      </FadeUp>
    </div>
  );
}
